import type { JSX } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { DetailPane } from "../../components/layout/DetailPane";
import { ListPane } from "../../components/layout/ListPane";
import {
  Icon,
  ModuleListItem,
  Row,
  SearchableTabs,
  Stack,
  Tag,
  TopBarHeader,
} from "../../components/ui";

interface SplitMenuItem {
  readonly label: string;
  readonly onClick: () => void;
}

function SplitButton({
  label,
  onClick,
  menu,
  disabled = false,
  disabledReason,
}: {
  readonly label: string;
  readonly onClick: () => void;
  readonly menu?: readonly SplitMenuItem[];
  readonly disabled?: boolean;
  readonly disabledReason?: string;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleDown(e: MouseEvent): void {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleKey(e: KeyboardEvent): void {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", handleDown);
    document.addEventListener("keydown", handleKey);
    return (): void => {
      document.removeEventListener("mousedown", handleDown);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  return (
    <div
      ref={wrapRef}
      className="relative inline-block"
      title={disabled ? disabledReason : undefined}
    >
      <div
        className={`inline-flex items-stretch rounded-[6px] border border-border-strong overflow-hidden text-[12px] h-6 ${
          disabled
            ? "text-content-tertiary opacity-50"
            : "text-content-secondary hover:text-content"
        }`}
      >
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          className="px-2.5 enabled:hover:bg-surface-hover disabled:cursor-not-allowed"
        >
          {label}
        </button>
        <div className="w-px bg-border-strong" />
        <button
          type="button"
          disabled={disabled}
          className="px-1.5 flex items-center enabled:hover:bg-surface-hover disabled:cursor-not-allowed"
          aria-label={`${label} options`}
          onClick={() => {
            setOpen((v) => !v);
          }}
        >
          <Icon name="chevron-down" size={11} />
        </button>
      </div>
      {open && menu && menu.length > 0 && (
        <div className="absolute left-0 top-[calc(100%+4px)] z-50 min-w-[210px] rounded-[10px] bg-surface-tertiary py-1 shadow-[0_8px_32px_rgba(0,0,0,0.65)]">
          {menu.map((item) => (
            <button
              key={item.label}
              type="button"
              className="flex w-full items-center px-3 py-1.5 text-left text-[13px] text-content hover:bg-white/[0.06]"
              onClick={() => {
                setOpen(false);
                item.onClick();
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
import { ConfigureSourceDialog } from "../settings/ConfigureSourceDialog";
import { useAnalytics } from "../../analytics/provider";
import { ModuleLayout } from "../../layout/ModuleLayout";
import { useAppRuntime } from "../../providers/AppRuntimeProvider";
import { useRouterContext } from "../../providers/RouterProvider";
import { ExtensionMark, isPreviewLocked, kindVariant } from "./helpers";
import {
  useExtensionsList,
  useInstallSkill,
  useSetExtensionEnabled,
  useUninstallSkill,
} from "./queries";
import { useExtensionsStore } from "./store";
import type { ExtensionView } from "./types";

function ExtensionRow({
  ext,
  selected,
  onClick,
}: {
  readonly ext: ExtensionView;
  readonly selected: boolean;
  readonly onClick: () => void;
}): JSX.Element {
  const previewLocked = isPreviewLocked(ext);
  return (
    <div className="cursor-pointer" onClick={onClick}>
      <ModuleListItem selected={selected}>
        <ExtensionMark ext={ext} size={42} />
        <div className="flex-1 min-w-0">
          <Row gap={1.5} align="center" className="min-w-0">
            <div
              className={`text-sm truncate ${
                selected ? "text-content font-medium" : "text-content-secondary"
              }`}
            >
              {ext.title}
            </div>
            <Tag
              label={ext.kind.toUpperCase()}
              variant={kindVariant(ext.kind)}
              mode="subtle"
            />
          </Row>
          {ext.summary && (
            <div className="text-[12px] text-content-tertiary truncate mt-0.5">
              {ext.summary}
            </div>
          )}
          <Row gap={1} align="center" className="mt-0.5">
            <span className="text-[11px] text-content-tertiary truncate">
              {ext.publisher}
            </span>
            <Icon
              name={previewLocked ? "lock" : "circle-check"}
              size={11}
              className={`${previewLocked ? "text-content-tertiary" : "text-blue"} flex-shrink-0`}
            />
          </Row>
        </div>
      </ModuleListItem>
    </div>
  );
}

function DetailActions({ ext }: { readonly ext: ExtensionView }): JSX.Element {
  const analytics = useAnalytics();
  const isHostedDemo = analytics.runtimeSurface === "hosted_demo";
  const hostedDemoReason = "Unavailable in Hosted Demo";
  const setEnabled = useSetExtensionEnabled();
  const install = useInstallSkill();
  const uninstall = useUninstallSkill();
  const [autoUpdate, setAutoUpdate] = useState(true);
  const [configuring, setConfiguring] = useState(false);

  const toggleEnabled = (
    <SplitButton
      label={ext.enabled ? "Disable" : "Enable"}
      disabled={isHostedDemo}
      disabledReason={hostedDemoReason}
      onClick={() => {
        setEnabled.mutate({ key: ext.id, enabled: !ext.enabled });
      }}
      menu={[
        {
          label: "Uninstall",
          onClick: (): void => {
            uninstall.mutate({ key: ext.id });
          },
        },
        {
          label: ext.enabled ? "Disable" : "Enable",
          onClick: (): void => {
            setEnabled.mutate({ key: ext.id, enabled: !ext.enabled });
          },
        },
      ]}
    />
  );

  const uninstallBtn = (
    <SplitButton
      label="Uninstall"
      disabled={isHostedDemo}
      disabledReason={hostedDemoReason}
      onClick={() => {
        uninstall.mutate({ key: ext.id });
      }}
      menu={[
        {
          label: "Uninstall",
          onClick: (): void => {
            uninstall.mutate({ key: ext.id });
          },
        },
        {
          label: "Install Specific Version…",
          onClick: (): void => {
            /* placeholder */
          },
        },
      ]}
    />
  );

  const autoUpdateControl = (
    <button
      type="button"
      disabled={isHostedDemo}
      title={isHostedDemo ? hostedDemoReason : undefined}
      onClick={() => {
        setAutoUpdate((v) => !v);
      }}
      className="flex items-center gap-1.5 text-[12px] text-content-secondary enabled:hover:text-content disabled:cursor-not-allowed disabled:opacity-50"
    >
      {autoUpdate ? (
        <Icon name="check-square" size={14} className="text-blue" />
      ) : (
        <span className="inline-block w-[14px] h-[14px] rounded-[3px] border border-border-strong" />
      )}
      Auto Update
    </button>
  );

  // Surface RPC failures (a race past the proactive gate) — cleaned of the
  // transport "RPC error NNN:" prefix — instead of a dead button.
  const actionError = isHostedDemo
    ? ""
    : (
        setEnabled.error?.message ??
        uninstall.error?.message ??
        install.error?.message ??
        ""
      ).replace(/^RPC error \d+:\s*/, "");
  const errorRow = actionError ? (
    <span className="text-red-400 text-[12px]" role="alert">
      {actionError}
    </span>
  ) : null;

  const blockedBadge = (label: string): JSX.Element => (
    <div className="inline-flex h-6 items-center gap-1.5 rounded-[6px] border border-border-strong bg-surface-sunken px-2.5 text-[12px] text-content-tertiary opacity-70">
      <Icon name="lock" size={12} />
      {label}
    </div>
  );
  const reasonRow = (text: string): JSX.Element => (
    <span className="text-content-tertiary text-[12px]">{text}</span>
  );
  const hostedDemoNotice = isHostedDemo ? (
    <span
      className="inline-flex items-center gap-1.5 text-content-tertiary text-[12px]"
      title={hostedDemoReason}
    >
      <Icon name="lock" size={12} />
      {hostedDemoReason}
    </span>
  ) : null;

  // Run axis (INV-L4): an installed source that is loaded but NOT admissible
  // (missing app credentials) can be installed but cannot START. Surface it
  // in red here — install ≠ run: the source appears in Accounts only once it
  // is actually running, so a needs-configuration source must say so plainly
  // instead of silently vanishing.
  const needsConfig =
    ext.kind === "source" &&
    (ext.connection?.startsWith("needs_configuration") ?? false);
  const isSource = ext.kind === "source";
  const isRunning = ext.connection === "running";

  // A plain "Needs configuration" flag next to Auto Update — no details, no
  // hover: clicking Enable opens the config dialog that asks for the fields.
  const alertIcon =
    needsConfig && !isRunning ? (
      <span className="inline-flex h-6 items-center gap-1.5 rounded-[6px] border border-red-500/40 bg-red-500/10 px-2.5 text-[12px] text-red-400">
        <Icon name="circle-alert" size={12} />
        Needs configuration
      </span>
    ) : null;

  // Sources: the Enable/Disable control reflects the RUN fact (`connection`),
  // NOT the stored enable flag. Clicking Enable RE-ATTEMPTS the start; if the
  // source still needs configuration it opens a popup naming what is missing
  // (we never silently no-op or decide for the user).
  const attemptStartOrStop = (): void => {
    if (isRunning) {
      setEnabled.mutate({ key: ext.id, enabled: false });
      return;
    }
    if (needsConfig) {
      // Not running because it needs configuration → open the config dialog
      // with the exact fields to enter, not a bare error.
      setConfiguring(true);
      return;
    }
    setEnabled.mutate({ key: ext.id, enabled: true });
  };
  const sourceToggle = (
    <SplitButton
      label={isRunning ? "Disable" : "Enable"}
      disabled={isHostedDemo}
      disabledReason={hostedDemoReason}
      onClick={attemptStartOrStop}
      menu={[
        {
          label: "Uninstall",
          onClick: (): void => {
            uninstall.mutate({ key: ext.id });
          },
        },
        {
          label: isRunning ? "Disable" : "Enable",
          onClick: attemptStartOrStop,
        },
      ]}
    />
  );

  // Fault states from the lifecycle registry (spec §3): say what is wrong
  // plainly instead of offering a working-looking action. The hand-set
  // "Not available in this demo" flag is gone (DEC-L10).
  if (
    ext.state === "broken" ||
    ext.state === "missing" ||
    ext.state === "version_conflict" ||
    ext.state === "migration_failed"
  ) {
    return (
      <Stack gap={1}>
        <Row gap={2} align="center">{blockedBadge(ext.state.replace("_", " "))}</Row>
        {ext.state_reason ? reasonRow(ext.state_reason) : null}
      </Stack>
    );
  }

  // Not installed + not installable (INV-L2: no declared lifecycle install)
  // → say so, never a working-looking Install.
  if (!ext.installed && ext.installable === false) {
    return (
      <Row gap={2} align="center">{blockedBadge("Not installable")}</Row>
    );
  }

  // A source shows TWO independent axes (INV-L4): install-state (from the
  // DB row) and the coarse connection (from the runtime probe). Management
  // happens in place — shared-provider keys in Settings → Sources, per-user
  // accounts in Accounts; the catalog only REFLECTS the state. An
  // available/tombstoned source DOES get Install (the standard host routine
  // re-creates its row) — fall through to the install branch below.
  // ONE lifecycle for module AND source: an installed source gets the SAME
  // Disable/Enable/Uninstall controls as a module (they apply LIVE — the
  // connector stops/starts). The catalog is a MARKETPLACE (VS Code 1:1) —
  // NO runtime status chips here; connection state lives in Settings.

  // A not-installed module (never installed, or uninstalled/tombstoned) →
  // (re)install. NOT "Demo Preview" (that vestigial label is gone — no valid
  // state maps to it).
  if (!ext.installed) {
    return (
      <Stack gap={1}>
        <Row gap={2} align="center" wrap>
          <SplitButton
            label="Install"
            disabled={isHostedDemo}
            disabledReason={hostedDemoReason}
            onClick={() => {
              install.mutate({ key: ext.id });
            }}
            menu={[
              {
                label: "Install Specific Version…",
                onClick: (): void => {
                  /* placeholder */
                },
              },
            ]}
          />
          {autoUpdateControl}
          {hostedDemoNotice}
        </Row>
        {errorRow}
      </Stack>
    );
  }

  // DEC-15: system-tier extensions cannot be disabled/uninstalled.
  if (!ext.removable) {
    return (
      <Stack gap={1}>
        <Row gap={2} align="center" wrap>
          {blockedBadge("System · required")}
          {autoUpdateControl}
          {hostedDemoNotice}
        </Row>
        {errorRow}
      </Stack>
    );
  }

  // DEC-16 proactive gating: block the primary action BEFORE the click and say
  // why. Disabling/uninstalling is blocked by enabled dependents; enabling is
  // blocked by unmet requirements. (Uninstall stays available when only enable
  // is blocked — a disabled leaf has no enabled dependents.)
  const dependents = ext.blocking_dependents ?? [];
  const requirements = ext.unmet_requirements ?? [];
  const disableBlocked = ext.enabled && dependents.length > 0;
  const enableBlocked = !ext.enabled && requirements.length > 0;
  const blockReason = disableBlocked
    ? `Required by: ${dependents.join(", ")} — disable ${
        dependents.length > 1 ? "them" : "it"
      } first`
    : enableBlocked
      ? `Requires: ${requirements.join(", ")} — enable ${
          requirements.length > 1 ? "them" : "it"
        } first`
      : null;

  return (
    <Stack gap={1}>
      <Row gap={2} align="center" wrap>
        {isSource ? (
          // Source: Enable/Disable reflects the RUN fact and stays ACTIVE —
          // clicking Enable re-attempts the start (popup on failure).
          <>
            {sourceToggle}
            {uninstallBtn}
          </>
        ) : disableBlocked ? (
          blockedBadge("Disable")
        ) : enableBlocked ? (
          <>
            {blockedBadge("Enable")}
            {uninstallBtn}
          </>
        ) : (
          <>
            {toggleEnabled}
            {uninstallBtn}
          </>
        )}
        {autoUpdateControl}
        {alertIcon}
        {hostedDemoNotice}
      </Row>
      {blockReason && reasonRow(blockReason)}
      {errorRow}
      {configuring ? (
        <ConfigureSourceDialog
          sourceId={ext.id.replace(/^source:[^.]+\./, "")}
          title={ext.title}
          onClose={() => {
            setConfiguring(false);
          }}
        />
      ) : null}
    </Stack>
  );
}

type DetailTab = "details" | "features" | "changelog";

const DETAIL_TABS: readonly {
  readonly id: DetailTab;
  readonly label: string;
}[] = [
  { id: "details", label: "Details" },
  { id: "features", label: "Features" },
  { id: "changelog", label: "Changelog" },
];

function ExtensionMarkdown({ text }: { readonly text: string }): JSX.Element {
  return (
    <div className="extension-markdown text-[14px] leading-relaxed text-content-secondary [&_p]:my-3 [&_ul]:my-3 [&_ul]:pl-5 [&_ul]:list-disc [&_li]:my-1 [&_h1]:text-lg [&_h1]:font-semibold [&_h1]:mt-5 [&_h1]:mb-2 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-4 [&_h2]:mb-2 [&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-1.5 [&_a]:text-blue [&_a]:hover:underline [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:bg-surface-sunken [&_code]:text-[12.5px]">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ node: _node, ...props }) => (
            <a {...props} target="_blank" rel="noreferrer noopener" />
          ),
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}

function stripLeadingTitle(md: string, title: string): string {
  const lines = md.split("\n");
  let i = 0;
  while (i < lines.length && (lines[i] ?? "").trim() === "") i += 1;
  if (i < lines.length) {
    const h1 = /^#\s+(.*)$/.exec((lines[i] ?? "").trim());
    if (h1?.[1]?.trim().toLowerCase() === title.trim().toLowerCase()) {
      lines.splice(0, i + 1);
      while (lines.length > 0 && (lines[0] ?? "").trim() === "") lines.shift();
    }
  }
  return lines.join("\n");
}

function DetailsBody({ ext }: { readonly ext: ExtensionView }): JSX.Element {
  const body = ext.details ? stripLeadingTitle(ext.details, ext.title) : "";
  const kindCopy: Record<ExtensionView["kind"], string> = {
    module:
      "A first-party Magnis module. Modules own a domain surface, persist their own facets in the shared graph, and expose a tool interface the agent can call directly.",
    source:
      "A data source connector. Sources hydrate the graph with external data (accounts, messages, files) and map remote identifiers to Magnis entities with full provenance.",
    skill:
      "A file-backed skill consumed by the agent runtime. Skills live on disk under the active skills directory and are discovered on every agent turn once installed.",
  };
  return (
    <Stack gap={5}>
      <div className="text-[14px] leading-relaxed text-content-secondary">
        {kindCopy[ext.kind]}
      </div>
      {body ? (
        <ExtensionMarkdown text={body} />
      ) : (
        <Stack gap={3}>
          <div className="text-[14px] leading-relaxed text-content-secondary">
            {ext.summary ||
              "No long-form description has been published for this extension yet."}
          </div>
          <div className="text-[14px] leading-relaxed text-content-secondary">
            Install state, version, and enablement are tracked in the
            <code className="mx-1 px-1 py-0.5 rounded bg-surface-sunken text-[12px]">
              installed_extensions
            </code>
            table. Toggling <span className="text-content">Disable</span> keeps
            the row so your preference survives a restart.
          </div>
        </Stack>
      )}
    </Stack>
  );
}

function FeaturesBody({ ext }: { readonly ext: ExtensionView }): JSX.Element {
  const items: readonly string[] =
    ext.kind === "module"
      ? [
          "Persists domain facets with full provenance",
          "Registers tools that the agent can call during a turn",
          "Exposes a settings schema for per-user configuration",
          "Subscribes to source deltas through the sync router",
        ]
      : ext.kind === "source"
        ? [
            "OAuth or token-based account registration",
            "Incremental sync with cursor-based resume",
            "Maps remote IDs to stable Magnis entity keys",
            "Idle until at least one account is connected",
          ]
        : [
            "Plain-markdown skill file discovered at agent boot",
            "Live enable/disable toggle — no restart required",
            "Isolated copy under the active skills directory",
            "Atomic install with rollback on failure",
          ];
  return (
    <Stack gap={2}>
      <div className="text-[14px] leading-relaxed text-content-secondary">
        {ext.title} ships with the following capabilities out of the box:
      </div>
      <ul className="list-disc pl-5 space-y-1.5 text-[14px] text-content-secondary">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </Stack>
  );
}

function ChangelogBody({ ext }: { readonly ext: ExtensionView }): JSX.Element {
  return (
    <Stack gap={3}>
      <Row gap={2} align="baseline">
        <div className="text-[15px] font-medium text-content">
          v{ext.version}
        </div>
        <div className="text-[12px] text-content-tertiary">current</div>
      </Row>
      <div className="text-[14px] leading-relaxed text-content-secondary">
        Initial release packaged with the Magnis build. This extension is
        distributed as part of the host binary; version numbers follow the
        application release cycle.
      </div>
    </Stack>
  );
}

function ExtensionDetailHeader({
  ext,
}: {
  readonly ext: ExtensionView;
}): JSX.Element {
  const previewLocked = isPreviewLocked(ext);
  return (
    <TopBarHeader
      leading={<ExtensionMark ext={ext} size={52} />}
      title={
        <Row gap={2} align="center">
          <span className="truncate">{ext.title}</span>
          <Tag
            label={ext.kind.toUpperCase()}
            variant={kindVariant(ext.kind)}
            mode="subtle"
          />
        </Row>
      }
      titleClassName="text-[18px] font-semibold leading-tight text-content"
      subtitle={
        <Row gap={1.5} align="center" className="min-w-0">
          {ext.publisher_url ? (
            <a
              href={ext.publisher_url}
              target="_blank"
              rel="noreferrer noopener"
              className="font-medium text-blue hover:underline truncate"
            >
              {ext.publisher}
            </a>
          ) : (
            <span className="font-medium text-blue truncate">
              {ext.publisher}
            </span>
          )}
          <Icon
            name={previewLocked ? "lock" : "circle-check"}
            size={12}
            className={previewLocked ? "text-content-tertiary" : "text-blue"}
          />
        </Row>
      }
      subtitleClassName="text-[12px] leading-tight mt-2"
      extra={
        <Stack gap={2} className="mt-2">
          {ext.summary && (
            <div className="text-[13px] leading-relaxed text-content-secondary">
              {ext.summary}
            </div>
          )}
          <DetailActions ext={ext} />
        </Stack>
      }
    />
  );
}

function DetailBody({ ext }: { readonly ext: ExtensionView }): JSX.Element {
  const [tab, setTab] = useState<DetailTab>("details");
  return (
    <Stack gap={0}>
      <SearchableTabs
        tabs={DETAIL_TABS}
        activeTab={tab}
        onTabChange={(id): void => {
          setTab(id as DetailTab);
        }}
        searchable
      />
      <div className="px-[10%] py-6">
        {tab === "details" && <DetailsBody ext={ext} />}
        {tab === "features" && <FeaturesBody ext={ext} />}
        {tab === "changelog" && <ChangelogBody ext={ext} />}
      </div>
    </Stack>
  );
}

export function ExtensionsPanel(): JSX.Element {
  const list = useExtensionsList();
  const router = useRouterContext();
  const searchQuery = useExtensionsStore((s) => s.searchQuery);
  const { setSearchQuery } = useExtensionsStore((s) => s.actions);
  const runtime = useAppRuntime();

  const selectedKey: string | undefined =
    router.activeModuleId === "extensions" &&
    router.entityType &&
    router.entityId
      ? `${router.entityType}:${router.entityId}`
      : undefined;

  const setSelectedKey = useCallback(
    (key: string | undefined): void => {
      if (!key) {
        router.setSelection("", undefined);
        return;
      }
      const idx = key.indexOf(":");
      if (idx < 0) return;
      const kind = key.slice(0, idx);
      const id = key.slice(idx + 1);
      router.setSelection(kind, id);
    },
    [router],
  );

  const allItems: readonly ExtensionView[] = useMemo(() => {
    const raw = list.data?.extensions ?? [];
    return [...raw].sort((a, b) => a.title.localeCompare(b.title));
  }, [list.data]);

  const filtered = useMemo(() => {
    if (!searchQuery) return allItems;
    const q = searchQuery.toLowerCase();
    return allItems.filter(
      (e) =>
        e.title.toLowerCase().includes(q) ||
        e.id.toLowerCase().includes(q) ||
        e.summary.toLowerCase().includes(q),
    );
  }, [allItems, searchQuery]);

  useEffect(() => {
    if (selectedKey) {
      if (!allItems.some((e) => e.id === selectedKey)) {
        setSelectedKey(allItems[0]?.id);
      }
      return;
    }
    if (allItems[0]) setSelectedKey(allItems[0].id);
  }, [allItems, selectedKey, setSelectedKey]);

  const selected = useMemo(
    () => allItems.find((e) => e.id === selectedKey) ?? null,
    [allItems, selectedKey],
  );

  useEffect(() => {
    runtime.agent.setActiveContext(
      selected
        ? {
            moduleId: "extensions",
            entityId: selected.id,
            entityTitle: selected.title,
          }
        : null,
    );
  }, [runtime, selected]);

  return (
    <ModuleLayout
      moduleName="Extensions"
      listPane={
        <ListPane title="Extensions" onSearch={setSearchQuery}>
          {filtered.map((ext) => (
            <ExtensionRow
              key={ext.id}
              ext={ext}
              selected={ext.id === selectedKey}
              onClick={() => {
                setSelectedKey(ext.id);
              }}
            />
          ))}
          {list.isLoading && (
            <div className="py-10 px-4 text-center text-content-tertiary text-sm">
              Loading…
            </div>
          )}
          {!list.isLoading && filtered.length === 0 && (
            <div className="py-10 px-4 text-center text-content-tertiary text-sm">
              No extensions match &ldquo;{searchQuery}&rdquo;
            </div>
          )}
        </ListPane>
      }
      detailPane={
        <DetailPane
          headerNode={
            selected ? <ExtensionDetailHeader ext={selected} /> : undefined
          }
        >
          {selected ? (
            <DetailBody ext={selected} />
          ) : (
            <div className="flex items-center justify-center h-full text-content-tertiary text-sm">
              Select an extension to see details.
            </div>
          )}
        </DetailPane>
      }
    />
  );
}
